import type { DetailRow, PaneData } from '../../shared/contracts';
import { detailRowsOf } from '../lib/pane-rows';
import { PaneFrame } from './PaneFrame';
import { EmptyRow } from './States';

/** Sticky-note tints/tilts cycled per note so the board reads as pinned paper, not a list. */
const NOTE_STYLES = [
  'bg-brand-amber-light/70 border-brand-amber/20 -rotate-1',
  'bg-brand-accent-light/80 border-brand-accent/15 rotate-[0.75deg]',
  'bg-brand-cream border-brand-primary/10 rotate-0',
  'bg-brand-accent-light/60 border-brand-accent/15 -rotate-[0.75deg]',
  'bg-brand-amber-light/50 border-brand-amber/20 rotate-1',
] as const;

/** Notice board: corporate items as clickable post-it notes (content opens in a modal). */
export function BoardCard({ pane, onOpslag, eyebrow }: { pane: PaneData; onOpslag?: (row: DetailRow) => void; eyebrow?: string }) {
  const rows = detailRowsOf(pane);
  return (
    <PaneFrame title={pane.title} source={pane.source} eyebrow={eyebrow} periodLabel={pane.periodLabel}>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {rows.map((row, i) => {
          const clickable = Boolean(row.html);
          const cls = `relative block h-full w-full rounded-lg border p-3 pt-4 text-left shadow-sm ${
            clickable ? 'transition-all hover:-translate-y-0.5 hover:rotate-0 hover:shadow-md' : ''
          } ${NOTE_STYLES[i % NOTE_STYLES.length]}`;
          const inner = (
            <>
              <span className="absolute left-1/2 top-1.5 h-2 w-2 -translate-x-1/2 rounded-full bg-brand-primary/50 shadow-sm" aria-hidden />
              <div className="text-[10px] font-medium uppercase tracking-wide text-brand-dark/50">{row.meta}</div>
              <div className="mt-0.5 line-clamp-2 text-sm font-medium leading-snug text-brand-dark">{row.title}</div>
              {row.subtitle && <div className="mt-0.5 line-clamp-1 text-xs text-brand-dark/55">{row.subtitle}</div>}
              {row.value && (
                <span className="mt-2 inline-block rounded-full bg-white/60 px-2 py-0.5 text-[10px] text-brand-dark/60">{row.value}</span>
              )}
            </>
          );
          return (
            <li key={row.id}>
              {clickable ? (
                <button type="button" onClick={() => onOpslag?.(row)} className={cls}>
                  {inner}
                </button>
              ) : (
                <div className={cls}>{inner}</div>
              )}
            </li>
          );
        })}
        {rows.length === 0 && <EmptyRow label="Ingen opslag." className="col-span-full" />}
      </ul>
    </PaneFrame>
  );
}
